import { addAllToRecord, addToRecord, objectWithoutKeys } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { CancelEventFulfilledAction } from '../../domains/cancelEvent/actions.types';
import type { CreateEventFulfilledAction } from '../../domains/createEvent/actions.types';
import type { EditEventFulfilledAction } from '../../domains/editEvent/actions.types';
import type { EventLoadByBroadcastIdFulfilledAction } from '../../domains/eventLoadByBroadcastId/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { LiveEventLoadByGroupIdFulfilledAction } from '../../domains/liveEventLoadByGroupId/actions.types';
import type { LiveEventLoadByNetworkFulfilledAction } from '../../domains/liveEventsLoadByNetwork/actions.types';
import type { LiveEventStatusUpdateAction } from '../../domains/liveEventsStatuses/actions.types';
import type { NavigationGroupsLoadFulfilledAction } from '../../domains/navigationGroupsLoad/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';

import type { LiveEventStatusInformation, LiveEventsStatuses } from './types';

export type LiveEventsStatusesAction =
  | CancelEventFulfilledAction
  | CreateEventFulfilledAction
  | EditEventFulfilledAction
  | EventLoadByBroadcastIdFulfilledAction
  | LiveEventStatusUpdateAction
  | LiveEventLoadByGroupIdFulfilledAction
  | LiveEventLoadByNetworkFulfilledAction
  | NavigationGroupsLoadFulfilledAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction;

const initialState: LiveEventsStatuses = {};

const stateWithUpdatedEventStatus = (state: LiveEventsStatuses, payload: LiveEventStatusInformation) =>
  addToRecord(state, payload.broadcastId, payload);

const stateWithEventsStatuses = (
  state: LiveEventsStatuses,
  action: Exclude<LiveEventsStatusesAction, CancelEventFulfilledAction | LiveEventStatusUpdateAction>
) => addAllToRecord(state, action.payload.entities.liveEventsStatuses);

const reducer: Reducer<LiveEventsStatuses, LiveEventsStatusesAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CANCEL_EVENT.FULFILLED':
      return objectWithoutKeys(state, [action.payload.broadcastId]);
    case 'EVENT.LOAD_BY_BROADCAST_ID_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'LIVE_EVENT_STATUS.UPDATE':
      return stateWithUpdatedEventStatus(state, action.payload);
    case 'LIVE_EVENT.LOAD_BY_GROUP_ID_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'NAVIGATION_GROUPS.LOAD_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'CREATE_EVENT.FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'EDIT_EVENT.FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'LIVE_EVENT.LOAD_BY_NETWORK_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'FEED.LOAD_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return stateWithEventsStatuses(state, action);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return stateWithEventsStatuses(state, action);
    default:
      return state;
  }
};

export default reducer;
